import { useUser } from "@clerk/clerk-react";
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import IntakeForm from "../components/organisms/IntakeForm";
import FormPageTemplate from "../components/templates/FormPageTemplate";
import { useTaskContext } from "../providers/TaskProvider";
import {
  insertTask,
  insertTaskAttachments,
  uploadMultipleFiles,
} from "../services/supabase";

export default function Intake() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const formRef = useRef(null);

  const navigate = useNavigate();
  const { user } = useUser();
  const { refreshTasks } = useTaskContext();

  const handleSubmit = async (formData) => {
    if (!formData.title.trim()) {
      setError("Title is required");
      return;
    }

    setIsSubmitting(true);
    setError("");

    try {
      // Create task object
      const taskData = {
        title: formData.title,
        description: formData.description,
        status: "Incoming",
        assignee_id: formData.assigneeId || null,
        created_by: user?.id || null,
        created_at: new Date().toISOString(),
      };

      // Insert task into database first
      const newTask = await insertTask(taskData);

      if (newTask) {
        // Handle file uploads if there are files
        if (formData.selectedFiles.length > 0) {
          const uploadedFileUrls = await uploadMultipleFiles(
            formData.selectedFiles
          );

          if (uploadedFileUrls.length > 0) {
            // Map URLs to file objects with names
            const fileObjects = uploadedFileUrls.map((url, index) => ({
              url,
              name: formData.selectedFiles[index].name,
            }));

            // Insert attachments into task_attachments table
            await insertTaskAttachments(newTask.id, fileObjects);
          }
        }

        // Refresh the task list to ensure the new task appears
        refreshTasks();

        setSuccess("Task created successfully!");

        // Reset form
        if (formRef.current) {
          formRef.current.resetForm();
        }

        // Navigate to dashboard after short delay
        setTimeout(() => {
          navigate("/");
        }, 1500);
      }
    } catch (err) {
      console.error("Error creating task:", err);
      setError("Failed to create task. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/");
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  };

  return (
    <FormPageTemplate
      title="New Task Intake"
      subtitle="Create a new task to be added to the Incoming column"
      error={error}
      success={success}
      onSubmit={handleFormSubmit}
      onCancel={handleCancel}
      isSubmitting={isSubmitting}
      submitButtonText="Create Task"
      cancelButtonText="Cancel"
    >
      <IntakeForm
        ref={formRef}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />
    </FormPageTemplate>
  );
}
